/**
 * Formatea un número de teléfono para WhatsApp.
 * Espera que el número ya tenga código de país (ej: +59899123456).
 * 
 * @param phone - Número de teléfono con código de país
 * @returns Número formateado para WhatsApp (solo dígitos, sin +)
 * 
 * @example
 * formatWhatsAppNumber('+59899123456') // '59899123456'
 * formatWhatsAppNumber('+5491112345678') // '5491112345678'
 */
export function formatWhatsAppNumber(phone: string): string {
  const { normalizePhone } = require('@/lib/phoneFormatter')
  const normalized = normalizePhone(phone)
  // WhatsApp necesita solo dígitos (sin +)
  return normalized.replace(/^\+/, '')
}

/**
 * Formatea un número de teléfono para mostrar con el prefijo +
 * Detecta códigos de país conocidos y usa Uruguay como default.
 * 
 * @param phone - Número de teléfono en cualquier formato
 * @returns Número normalizado con + (ej: +59899123456, +5491112345678)
 */
export function formatPhoneDisplay(phone: string): string {
  if (!phone) return ''
  const { normalizePhone } = require('@/lib/phoneFormatter')
  return normalizePhone(phone)
}

/**
 * Formatea un número de teléfono para el atributo href de tel:
 * Asegura que el número tenga el prefijo + y el código de país
 * 
 * @param phone - Número de teléfono en cualquier formato
 * @returns Número formateado para tel: link (ej: +59899123456)
 */
export function formatPhoneLink(phone: string): string {
  return formatPhoneDisplay(phone)
}

/**
 * Parsea una fecha en formato YYYY-MM-DD en la zona horaria local
 * Evita problemas de conversión de timezone al crear el objeto Date
 * 
 * @param dateString - Fecha en formato YYYY-MM-DD
 * @returns Objeto Date en la zona horaria local
 * 
 * @example
 * parseDateInLocalTimezone('2026-01-31') // Date object for Jan 31, 2026 at 00:00 local time
 */
export function parseDateInLocalTimezone(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Parsea una fecha y hora en la zona horaria local
 * Evita problemas de conversión de timezone al crear el objeto Date
 * 
 * @param dateString - Fecha en formato YYYY-MM-DD
 * @param timeString - Hora en formato HH:MM o HH:MM:SS
 * @returns Objeto Date en la zona horaria local
 * 
 * @example
 * parseDateTimeInLocalTimezone('2026-01-31', '14:30') // Date object for Jan 31, 2026 at 14:30 local time
 * parseDateTimeInLocalTimezone('2026-01-31', '14:30:00') // Date object for Jan 31, 2026 at 14:30:00 local time
 */
export function parseDateTimeInLocalTimezone(dateString: string, timeString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  const timeParts = timeString.split(':').map(Number)
  const [hours, minutes, seconds = 0] = timeParts
  return new Date(year, month - 1, day, hours, minutes, seconds)
}

/**
 * Convierte un objeto Date a formato ISO de fecha (YYYY-MM-DD)
 * Usa la fecha local del objeto Date, no UTC
 * 
 * @param date - Objeto Date
 * @returns Fecha en formato YYYY-MM-DD
 * 
 * @example
 * formatDateToISO(new Date(2026, 0, 31)) // '2026-01-31'
 */
export function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Formatea una fecha en español
 * 
 * @param date - Fecha como Date o string ISO
 * @param options - Opciones de formato (por defecto: día completo, día, mes, año)
 * @returns Fecha formateada en español
 * 
 * @example
 * formatDate(new Date()) // 'lunes, 3 de diciembre de 2025'
 * formatDate('2025-12-03', { weekday: 'short', day: 'numeric', month: 'short' }) // 'lun, 3 dic'
 */
export function formatDate(
  date: Date | string | undefined | null,
  options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }
): string {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  return dateObj.toLocaleDateString('es-ES', options)
}

/**
 * Formatea una hora en formato 24h
 * 
 * @param time - Hora como Date o string ISO
 * @returns Hora formateada en formato HH:MM
 * 
 * @example
 * formatTime(new Date()) // '14:30'
 * formatTime('2025-12-03T14:30:00') // '14:30'
 */
export function formatTime(time: Date | string): string {
  const timeObj = typeof time === 'string' ? new Date(time) : time
  return timeObj.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Obtiene la URL base de la API
 * 
 * @returns URL base de la API
 */
function getApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return process.env.NEXT_PUBLIC_API_URL || ''
  }
  return window.location.origin
}

/**
 * Construye la URL completa para una imagen
 * Si la URL ya es completa (http/https), la devuelve tal cual
 * Si es una ruta relativa, construye la URL completa con /uploads/
 *
 * @param imageUrl - Ruta de la imagen (relativa o absoluta)
 * @param size - Tamaño opcional: 'thumb' (128px), 'small' (256px), 'medium' (640px). Si se omite, sirve la imagen original.
 * @returns URL completa de la imagen
 *
 * @example
 * getImageUrl('barbers/tenant-id/photo.jpg') // 'https://domain.com/uploads/barbers/tenant-id/photo.jpg'
 * getImageUrl('barbers/tenant-id/photo.jpg', 'thumb') // 'https://domain.com/uploads/barbers/tenant-id/photo.jpg?size=thumb'
 * getImageUrl('barbers/tenant-id/photo.jpg', 'medium') // 'https://domain.com/uploads/barbers/tenant-id/photo.jpg?size=medium'
 * getImageUrl('https://example.com/photo.jpg') // 'https://example.com/photo.jpg'
 */
export function getImageUrl(imageUrl: string | null | undefined, size?: 'thumb' | 'small' | 'medium'): string {
  if (!imageUrl) return ''

  // Si es una URL completa, devolverla tal cual (no se puede agregar resize a URLs externas)
  if (imageUrl.startsWith('http')) {
    return imageUrl
  }

  // Si es una ruta relativa, usar /uploads/ directamente (Traefik enruta a la API)
  const url = `/uploads/${imageUrl}`
  return size ? `${url}?size=${size}` : url
}
